import { ReactNode } from 'react';
import { Visibility } from '@mui/icons-material';
import styled from '@emotion/styled';
import { uiTheme as t } from './theme';
import EmptyState from './EmptyState';
import Checkbox from './Checkbox';

export interface DataTableRow {
  id: string;
  /** El resto de campos los lee cada columna por su `key` o con `render`. */
  [field: string]: unknown;
}

export interface DataTableColumn {
  /** Campo de la fila, y clave de React de la celda. */
  key: string;
  label: string;
  /** Fracción de la rejilla, p. ej. '1.5fr' o '80px'. */
  width: string;
  /** Destaca la celda (la usa la columna que nombra el registro). */
  strong?: boolean;
  /** Para celdas que no son texto plano: insignias, estados, formatos. */
  render?: (row: DataTableRow) => ReactNode;
}

export interface DataTableLabels {
  empty: string;
  selectAll?: string;
  viewDetails?: string;
}

interface DataTableProps {
  rows: DataTableRow[];
  columns: DataTableColumn[];
  labels: DataTableLabels;
  /** Si llega, la tabla añade la columna de casillas para acciones en lote. */
  selection?: {
    selected: Set<string>;
    onToggle: (id: string) => void;
    onToggleAll: () => void;
  };
  /**
   * Contenido al final de cada fila: un menú de acciones, un botón, lo que sea.
   *
   * Es la ranura genérica. `onViewDetails` es un atajo construido encima, para
   * el caso frecuente de entrar al detalle de un registro.
   */
  rowActions?: (row: DataTableRow) => ReactNode;
  /** Si llega, cada fila cierra con el botón que entra al detalle. */
  onViewDetails?: (row: DataTableRow) => void;
}

const Surface = styled.div({
  marginTop: 20,
  background: t.color.surface,
  border: `1px solid ${t.color.border}`,
  borderRadius: t.radius.lg,
  overflow: 'hidden',
  boxShadow: t.shadow.card,
});

// El reparto de columnas va por `style`: emotion reenviaría al DOM una prop
// `template` y React se quejaría de un atributo desconocido en el div.
const HeadRow = styled.div({
  display: 'grid',
  alignItems: 'center',
  gap: 16,
  padding: '14px 24px',
  background: t.color.tableHeadBg,
  borderBottom: `1px solid ${t.color.rowBorder}`,
});

const HeadCell = styled.span({
  fontFamily: t.font,
  fontSize: 11.5,
  fontWeight: 600,
  letterSpacing: '0.6px',
  color: t.color.tableHeadText,
});

const BodyRow = styled.div({
  display: 'grid',
  alignItems: 'center',
  gap: 16,
  padding: '15px 24px',
  borderTop: `1px solid ${t.color.rowBorder}`,
  '&:hover': { background: t.color.rowHoverBg },
});

const Cell = styled.div<{ strong?: boolean }>(({ strong }) => ({
  fontFamily: t.font,
  fontSize: strong ? 14.5 : 13.5,
  fontWeight: strong ? 600 : 400,
  color: strong ? t.color.title : t.color.text,
  whiteSpace: 'nowrap',
  overflow: 'hidden',
  textOverflow: 'ellipsis',
}));

const Actions = styled.div({
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'flex-end',
});

const IconButton = styled.button({
  display: 'inline-flex',
  alignItems: 'center',
  justifyContent: 'center',
  border: 'none',
  background: 'transparent',
  color: t.color.muted,
  cursor: 'pointer',
  padding: 6,
  borderRadius: t.radius.xs,
  transition: 'color 0.15s ease, background 0.15s ease',
  '& .MuiSvgIcon-root': { fontSize: 20 },
  '&:hover': { color: t.color.primary, background: t.color.countBg },
});

/**
 * Organismo: tabla de datos con columnas configurables.
 *
 * Nació como el listado de puestos y departamentos —que hacían lo mismo con
 * distintas columnas— y se generalizó al necesitarla también el plan de
 * desarrollo, que no entra a ningún detalle sino que abre un menú de acciones.
 * Por eso el final de fila es una ranura (`rowActions`) y no un botón fijo.
 *
 * Entrar al detalle nunca es la fila entera, siempre el control del final:
 * donde hay casillas, un clic perdido sacaría de la pantalla al administrador
 * con media selección hecha.
 */
const DataTable = ({ rows, columns, labels, selection, rowActions, onViewDetails }: DataTableProps) => {
  const hasTrailingCell = Boolean(rowActions) || Boolean(onViewDetails);

  const gridTemplateColumns = [
    selection ? '38px' : null,
    ...columns.map(column => column.width),
    hasTrailingCell ? '60px' : null,
  ]
    .filter(Boolean)
    .join(' ');

  const allSelected = Boolean(selection) && rows.length > 0 && rows.every(row => selection?.selected.has(row.id));

  const renderTrailingCell = (row: DataTableRow) => {
    if (rowActions) return <Actions>{rowActions(row)}</Actions>;
    if (!onViewDetails) return null;

    return (
      <Actions>
        <IconButton type="button" aria-label={labels.viewDetails} onClick={() => onViewDetails(row)}>
          <Visibility />
        </IconButton>
      </Actions>
    );
  };

  return (
    <Surface>
      <HeadRow style={{ gridTemplateColumns }}>
        {selection ? (
          <Checkbox checked={allSelected} onChange={selection.onToggleAll} aria-label={labels.selectAll} />
        ) : null}
        {columns.map(column => (
          <HeadCell key={column.key}>{column.label}</HeadCell>
        ))}
        {hasTrailingCell ? <span /> : null}
      </HeadRow>

      {rows.map(row => (
        <BodyRow key={row.id} style={{ gridTemplateColumns }}>
          {selection ? (
            <Checkbox
              checked={selection.selected.has(row.id)}
              onChange={() => selection.onToggle(row.id)}
              aria-label={String(row[columns[0].key] ?? row.id)}
            />
          ) : null}
          {columns.map(column =>
            column.render ? (
              <div key={column.key}>{column.render(row)}</div>
            ) : (
              <Cell key={column.key} strong={column.strong}>
                {(row[column.key] as ReactNode) ?? '—'}
              </Cell>
            )
          )}
          {renderTrailingCell(row)}
        </BodyRow>
      ))}

      {rows.length === 0 ? <EmptyState message={labels.empty} /> : null}
    </Surface>
  );
};

export default DataTable;
